import React, { useState, useEffect } from 'react';
import { FiPrinter, FiSearch } from 'react-icons/fi';
import api from '../../utils/api';
import '../../styles/mobileResponsive.css';

const font = "'Outfit', sans-serif";
const C = { text: '#1a0020', sub: '#64748b', border: '#e9d5ff', bg: '#fdf4ff', white: '#fff', accent: '#a855f7' };

const SeatSheet = () => {
    const [assignments, setAssignments] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [seats, setSeats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const load = async () => {
        try {
            const r = await api.get('/api/invigilator/my-assignments');
            setAssignments(r.data.assignments || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const selectHall = async (hall) => {
        setLoading(true);
        try {
            const sr = await api.get(`/api/invigilator/assignment/${hall.id}/seats`);
            setSeats(sr.data.seats);
            setSelectedAssignment(hall);
            setSearch('');
        } catch (e) { 
            console.error(e); 
            alert('Failed to load seats for this hall.');
        } finally { setLoading(false); }
    };

    const print = () => window.print();

    const filtered = seats.filter(s =>
        s.student_name?.toLowerCase().includes(search.toLowerCase()) ||
        s.roll_number?.toLowerCase().includes(search.toLowerCase()) ||
        String(s.seat_number).includes(search)
    );

    if (loading && assignments.length === 0) return <div style={{ textAlign: 'center', padding: 60, color: C.sub, fontFamily: font }}>Loading assignments...</div>;

    // ─── LIST VIEW ───
    if (!selectedAssignment) return (
        <div style={{ fontFamily: font }}>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: '0 0 6px' }}>Seat Sheets</h1>
            <p style={{ color: C.sub, fontSize: 13, margin: '0 0 20px' }}>Select a hall to view and print its seat sheet.</p>

            {assignments.length === 0 ? (
                <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, padding: 50, textAlign: 'center' }}>
                    <p style={{ color: C.sub }}>No assignments found. Please contact the Exam Controller.</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                    {assignments.map(a => (
                        <div key={a.id} onClick={() => selectHall(a)} className="card-hover" style={{
                            background: C.white, borderRadius: 18, border: `1.5px solid ${C.border}`, 
                            padding: 20, cursor: 'pointer', boxShadow: '0 4px 12px rgba(168,85,247,0.05)'
                        }}>
                            <div style={{ fontSize: 20, fontWeight: 900, color: C.accent, marginBottom: 8 }}>Hall {a.hall_number}</div>
                            <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 4 }}>{a.subject_name}</div>
                            <div style={{ fontSize: 12, color: C.sub, marginBottom: 12 }}>{a.subject_code} • {a.batch_name}</div>
                            <div style={{ fontSize: 11, color: C.sub }}>{a.exam_date?.slice(0, 10)} • {a.exam_time}</div>
                            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}`, color: C.accent, fontSize: 12, fontWeight: 700 }}>
                                View Seat Sheet →
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ─── DETAIL VIEW (Seat Sheet) ───
    return (
        <div style={{ fontFamily: font }}>
            <button onClick={() => setSelectedAssignment(null)} className="resp-hide-print" style={{ background: 'none', border: 'none', color: C.accent, fontWeight: 800, fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
                ← Back to List
            </button>

            <div className="resp-page-header">
                <div>
                    <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: '0 0 4px' }}>Seat Sheet: Hall {selectedAssignment.hall_number}</h1>
                    <p style={{ color: C.sub, fontSize: 13, margin: 0 }}>{selectedAssignment.subject_name} ({selectedAssignment.subject_code}) • {selectedAssignment.exam_date?.slice(0, 10)}</p>
                </div>
                <button onClick={print} className="resp-hide-print" style={{ padding: '10px 20px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#c084fc,#a855f7)', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(168,85,247,0.35)', whiteSpace: 'nowrap' }}>
                    <FiPrinter size={15} /> Print Seat Sheet
                </button>
            </div>

            <div className="resp-grid-4" style={{ marginBottom: 20 }}>
                {[
                    { label: 'Hall', val: selectedAssignment.hall_number },
                    { label: 'Subject', val: `${selectedAssignment.subject_code}` },
                    { label: 'Department', val: selectedAssignment.department },
                    { label: 'Total Students', val: seats.length },
                ].map((item, i) => (
                    <div key={i} style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, padding: '14px 16px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{item.label}</div>
                        <div style={{ fontSize: 15, fontWeight: 900, color: C.text, wordBreak: 'break-word' }}>{item.val}</div>
                    </div>
                ))}
            </div>

            <div className="resp-hide-print" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12, marginBottom: 20 }}>
                <FiSearch size={16} color={C.sub} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, roll, or seat..." style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontSize: 13, fontFamily: font, color: C.text }} />
            </div>

            <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                <div className="resp-seat-header" style={{ background: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: 11, fontWeight: 800, color: C.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <div>Seat</div>
                    <div>Student Name</div>
                    <div>Roll Number</div>
                    <div className="resp-hide-mobile">Anonymous ID</div>
                    <div className="resp-hide-mobile">Signature</div>
                </div>

                {filtered.length === 0 ? (
                    <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontSize: 13 }}>No students allocated yet.</div>
                ) : (
                    filtered.map((s, idx) => (
                        <div key={s.id} className="resp-seat-row" style={{ borderBottom: `1px solid ${C.border}`, background: idx % 2 === 1 ? '#fdf4ff' : C.white }}>
                            <div style={{ fontSize: 17, fontWeight: 900, color: C.accent }}>{s.seat_number}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                                {s.student_name}
                                <div className="resp-show-mobile" style={{ fontSize: 10, fontWeight: 800, color: '#6b21a8', background: '#f3e8ff', padding: '2px 8px', borderRadius: 20, display: 'inline-block', marginLeft: 6 }}>{s.anonymous_id}</div>
                            </div>
                            <div style={{ fontSize: 12, color: C.sub }}>{s.roll_number}</div>
                            <span className="resp-hide-mobile" style={{ fontSize: 11, fontWeight: 800, color: '#6b21a8', background: '#f3e8ff', padding: '4px 10px', borderRadius: 20, display: 'inline-block' }}>{s.anonymous_id}</span>
                            <div className="resp-hide-mobile" style={{ width: 100, height: 36, borderBottom: `1.5px solid ${C.border}` }}></div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default SeatSheet;
